import { useState, useEffect } from "react";

interface PinVerifyScreenProps {
  /** Shown as the title above the dots */
  title?: string;
  onSuccess: () => void;
  onVerify: (pin: string) => boolean;
}

const NUMPAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];
const MAX_ATTEMPTS = 5;

/**
 * Full-screen PIN entry overlay for exiting Kid Mode or accessing adult nav.
 * Renders on top of everything — use inside a portal or at the root of KidLayout.
 */
export function PinVerifyScreen({
  title = "Enter PIN",
  onSuccess,
  onVerify,
}: PinVerifyScreenProps) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (pin.length === 4) {
      const correct = onVerify(pin);
      if (correct) {
        onSuccess();
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= MAX_ATTEMPTS) {
          setLocked(true);
          setError("Too many attempts. Ask a parent for help.");
        } else {
          setError(`Incorrect PIN (${MAX_ATTEMPTS - newAttempts} tries left)`);
        }
        triggerShake();
        setPin("");
      }
    }
  }, [pin]);

  function handleKey(key: string) {
    if (locked) return;
    if (key === "⌫") {
      setPin((p) => p.slice(0, -1));
      setError("");
      return;
    }
    if (key === "" || pin.length >= 4) return;
    setPin((p) => p + key);
  }

  function triggerShake() {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  }

  const dots = Array.from({ length: 4 }, (_, i) => i < pin.length);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background">
      <div className="w-full max-w-xs px-6 flex flex-col items-center gap-6">
        {/* Lock icon */}
        <div className="text-5xl">🔒</div>

        <h2 className="text-2xl font-bold text-center">{title}</h2>

        {/* Dot indicators */}
        <div className={`flex gap-4 ${shake ? "animate-shake" : ""}`}>
          {dots.map((filled, i) => (
            <div
              key={i}
              className={`w-5 h-5 rounded-full border-2 transition-colors ${
                filled
                  ? "bg-emerald-500 border-emerald-500"
                  : "border-muted-foreground"
              }`}
            />
          ))}
        </div>

        {error && (
          <p className="text-center text-sm text-destructive">{error}</p>
        )}

        {/* Numpad */}
        {!locked && (
          <div className="grid grid-cols-3 gap-3 w-full">
            {NUMPAD.map((key, i) => (
              <button
                key={i}
                onClick={() => handleKey(key)}
                disabled={key === ""}
                className={`h-16 rounded-2xl text-2xl font-semibold transition-all ${
                  key === ""
                    ? "invisible"
                    : key === "⌫"
                    ? "bg-muted hover:bg-muted/80 text-muted-foreground text-lg"
                    : "bg-muted hover:bg-accent active:scale-95 shadow-sm"
                }`}
              >
                {key}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
